import { SCENARIO_UNIT_BARRELS } from './scenarioUnits';

export const ECONOMY_TEMPLATE_VISIBILITY = {
  PUBLIC: '53184dbd-ff42-497e-8137-a89dda5e5f87',
  COMPANY_PUBLIC: '20a7f07b-93c4-4188-93c1-920bbc8796c8',
  PRIVATE: '8e3819e8-4fb5-4eef-9bcb-188e9f7059e9',
} as const;

export const FIELD_ECONOMY_PARAM_ORDER = [
  'discount_rate',
  'royalty_rate',
  'income_tax_rate',
  'transport_per_unit',
  'fixed_field_opex_per_year',
] as const;

export const LAYER_ECONOMY_PARAM_ORDER = [
  'capex_production_well',
  'capex_injection_well',
  'opex_production_well',
  'opex_injection_well',
  'oil_price',
  'injection_unit_cost',
  'abandonment_per_well',
  'additional_cost_per_active_well',
  'additional_cost_per_year',
  'additional_cost_per_oil_unit',
] as const;

export function sortEconomyAttributes<T extends { name: string }>(
  attributes: T[],
  order: readonly string[],
): T[] {
  const rank = new Map(order.map((name, index) => [name, index]));
  return [...attributes].sort(
    (a, b) => (rank.get(a.name) ?? 999) - (rank.get(b.name) ?? 999),
  );
}

function oilUnit(unitId: number): string {
  return unitId === SCENARIO_UNIT_BARRELS ? 'bbl' : 't';
}

export function getEconomyAttributeLabel(name: string, unitId = 1): string {
  switch (name) {
    case 'discount_rate':
      return 'Discount rate (%)';
    case 'royalty_rate':
      return 'Royalty (%)';
    case 'income_tax_rate':
      return 'Income tax (%)';
    case 'transport_per_unit':
      return `Transport (${oilUnit(unitId)})`;
    case 'fixed_field_opex_per_year':
      return 'Fixed field OPEX / year';
    case 'capex_production_well':
      return 'CAPEX — production well';
    case 'capex_injection_well':
      return 'CAPEX — injection well';
    case 'opex_production_well':
      return 'OPEX / year — production well';
    case 'opex_injection_well':
      return 'OPEX / year — injection well';
    case 'oil_price':
      return `Oil price (${oilUnit(unitId)})`;
    case 'injection_unit_cost':
      return 'Injection cost (m³)';
    case 'abandonment_per_well':
      return 'Abandonment / well';
    case 'additional_cost_per_active_well':
      return 'Additional / active well / year';
    case 'additional_cost_per_year':
      return 'Additional / year';
    case 'additional_cost_per_oil_unit':
      return `Additional / oil (${oilUnit(unitId)})`;
    default:
      return name;
  }
}

export function getEconomyAttributePlaceholder(name: string, unitId = 1): string {
  const u = oilUnit(unitId);
  switch (name) {
    case 'discount_rate':
      return '10';
    case 'royalty_rate':
      return '30';
    case 'income_tax_rate':
      return '20';
    case 'transport_per_unit':
      return unitId === SCENARIO_UNIT_BARRELS ? '50' : '370';
    case 'fixed_field_opex_per_year':
      return '3000000';
    case 'capex_production_well':
    case 'capex_injection_well':
      return '3000000';
    case 'opex_production_well':
      return '200000';
    case 'opex_injection_well':
      return '150000';
    case 'oil_price':
      return unitId === SCENARIO_UNIT_BARRELS ? '70' : '500';
    case 'injection_unit_cost':
      return '3';
    case 'abandonment_per_well':
      return '1000000';
    case 'additional_cost_per_active_well':
    case 'additional_cost_per_year':
    case 'additional_cost_per_oil_unit':
      return '0 — leave empty if not used';
    default:
      return '';
  }
}

export function getEconomyAttributeHelperText(
  name: string,
  unitId = 1,
  templateType: 'field' | 'layer' = 'field',
): string {
  const required = isRequiredForEconomicsCalculation(name, templateType);
  const optionalNote = required ? 'Required for economics calculation.' : 'Optional.';
  const u = oilUnit(unitId);
  switch (name) {
    case 'discount_rate':
      return `${optionalNote} Annual discount rate for NPV. Enter as percent, e.g. 10 for 10%.`;
    case 'royalty_rate':
      return `${optionalNote} Mineral royalty as percent of gross revenue.`;
    case 'income_tax_rate':
      return `${optionalNote} Tax on operating profit before CAPEX, in percent.`;
    case 'transport_per_unit':
      return `${optionalNote} Transport cost per ${u} of oil produced.`;
    case 'fixed_field_opex_per_year':
      return `${optionalNote} Fixed field-level operating cost per calendar year.`;
    case 'capex_production_well':
      return `${optionalNote} One-time capital cost when a production well is drilled.`;
    case 'capex_injection_well':
      return `${optionalNote} One-time capital cost when an injection well is drilled.`;
    case 'opex_production_well':
      return `${optionalNote} Annual operating cost per active production well.`;
    case 'opex_injection_well':
      return `${optionalNote} Annual operating cost per active injection well.`;
    case 'oil_price':
      return `${optionalNote} Oil price per ${u} in template currency.`;
    case 'injection_unit_cost':
      return `${optionalNote} Cost per m³ of injected liquid.`;
    case 'abandonment_per_well':
      return `${optionalNote} One-time cost when a well is shut down.`;
    case 'additional_cost_per_active_well':
      return `${optionalNote} Extra cost per active well per year.`;
    case 'additional_cost_per_year':
      return `${optionalNote} Extra fixed cost per calendar year for the layer.`;
    case 'additional_cost_per_oil_unit':
      return `${optionalNote} Extra cost per ${u} of oil produced.`;
    default:
      return optionalNote;
  }
}

export function isRequiredForEconomicsCalculation(name: string, templateType: 'field' | 'layer'): boolean {
  if (templateType === 'field') {
    return name === 'discount_rate';
  }
  return ![
    'additional_cost_per_active_well',
    'additional_cost_per_year',
    'additional_cost_per_oil_unit',
  ].includes(name);
}

export function getEconomyTemplateTypeLabel(templateType: 'field' | 'layer'): string {
  return templateType === 'field' ? 'Field economics' : 'Layer economics';
}
